import { stripJsonContentTypeForFormData } from './formDataContentType';
import { friendlyAdminErrorMessage } from './adminErrorMessages';
import { friendlyServerErrorMessage } from './serverErrorMessages';
import { errorsStrings } from '@/i18n/dictionaries/errors';
import { pickStrings } from '@/i18n/define';
import { isRefreshExemptUrl } from './refreshExemptPaths';
import { createSessionRefresh, SESSION_REFRESH_PASS } from './sessionRefresh';
import {
  isEmailNotVerifiedFallbackExemptUrl,
  isEmailNotVerifiedFallbackExemptLocation,
  emailNotVerifiedRedirectUrl,
} from './emailNotVerifiedFallback';
import { resolveEmailNotVerifiedRetry } from './emailNotVerifiedRetry';
import { readBlobErrorBody } from './blobErrorBody';
import {
  CLIENT_TIMEOUT_DEFAULTS,
  isRequestTimeoutError,
} from './requestTimeout';
import { requestTimeoutMessage } from './requestTimeoutMessage';
import type { EmailNotVerifiedRetryDeps } from './emailNotVerifiedRetry';
import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
// WEB-1988: сообщения человеку клиент отдаёт наружу, а не рисует сам. Раньше
// здесь стоял `message` из Ant Design — и любой импорт клиента тянул всю
// библиотеку в бандл покупателя воронки (второй вход сборки, public.html).
// Кто показывает — решает вход: кабинет подключает antd-показчик в main.tsx
// (installAntdApiNotifier), публичный вход не подключает никого.
import { notifyApiError, notifyApiWarning } from './uiNotify';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export const TOKEN_KEY = 'access_token';
export const CURRENT_PROJECT_KEY = 'current_project_id';
// WEB-1114 — the server has always issued a rotating refresh token
// (POST /auth/refresh); the client just never stored or used it. See
// screen-map-auth.md for the forced-logout toast this unlocks.
export const REFRESH_TOKEN_KEY = 'refresh_token';

export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  remove: () => localStorage.removeItem(TOKEN_KEY),
};

export const refreshTokenStorage = {
  get: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  set: (token: string) => localStorage.setItem(REFRESH_TOKEN_KEY, token),
  remove: () => localStorage.removeItem(REFRESH_TOKEN_KEY),
};

// WEB-1114 — client.ts cannot import the Redux store directly: store/index.ts
// combines authSlice, which imports tokenStorage/refreshTokenStorage FROM
// this file — a direct import the other way would cycle. store/index.ts
// registers the real handler once the store exists (dispatches logoutThunk,
// reusing its existing full-state wipe — see rootReducer in store/index.ts).
let sessionExpiredHandler: (() => void) | null = null;
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  sessionExpiredHandler = handler;
}

// Centralised here so logoutThunk (authSlice) can clear it without creating
// a cycle with projectsSlice. The selected project id MUST be wiped at logout
// — otherwise a different user logging in afterwards inherits the previous
// session's projectId and the backend returns 403 "not a member of project".
export const projectStorage = {
  get: (): string | null => {
    try {
      return localStorage.getItem(CURRENT_PROJECT_KEY);
    } catch {
      return null;
    }
  },
  set: (id: string) => {
    try {
      localStorage.setItem(CURRENT_PROJECT_KEY, id);
    } catch {
      /* ignore */
    }
  },
  remove: () => {
    try {
      localStorage.removeItem(CURRENT_PROJECT_KEY);
    } catch {
      /* ignore */
    }
  },
};

export const api = axios.create({
  // WEB-2590: общий предел ожидания. Повисший запрос (отметка версии после
  // «Оставить мои правки», перечитывание черновика) раньше держал
  // автосохранение и каркас загрузки бесконечно. Долгим по природе запросам
  // предел дают из реестра api/requestTimeout.ts (longRequest).
  ...CLIENT_TIMEOUT_DEFAULTS,
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// WEB-971: FormData не должен уходить с дефолтным application/json —
// иначе multipart без boundary и «Файл не передан» на бэке.
api.interceptors.request.use(stripJsonContentTypeForFormData);

api.interceptors.request.use((config) => {
  const token = tokenStorage.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// WEB-1114 — silent session renewal; WEB-2613 — with the common request
// time limit. The refresh call itself (a bare transport, not `api`, so it
// neither re-enters this interceptor nor carries the stale Authorization
// header) and the 401 decision live in sessionRefresh.ts, which jest can load.
// A hung or dropped refresh no longer ends the session: waiting requests get
// a dictionary refusal, the next attempt starts at once; only a server answer
// «this refresh token is no good» logs the user out.
const sessionRefresh = createSessionRefresh({
  baseURL: API_BASE_URL,
  getStoredRefreshToken: refreshTokenStorage.get,
  onRefreshed: ({ accessToken, refreshToken }) => {
    tokenStorage.set(accessToken);
    refreshTokenStorage.set(refreshToken);
  },
  onSessionExpired: () => {
    tokenStorage.remove();
    refreshTokenStorage.remove();
    // AC4 — a technical 401 must not surface to the user; explain why they
    // landed back on /login instead.
    notifyApiWarning(pickStrings(errorsStrings).sessionExpired);
    sessionExpiredHandler?.();
  },
  resend: (config) => api(config),
  isExemptUrl: isRefreshExemptUrl,
});

// WEB-2988 (решение владельца 14.10, вариант «а») — экран подтверждения почты
// с одиноким пропуском обновления сперва обновляет сессию, потом выходит с
// новым пропуском доступа: иначе POST /auth/logout уходит без заголовка,
// сервер отвечает 401 и пропуск обновления не отзывает. Тот же координатор,
// что у ветки 401 (одно обновление на всех, новая пара — в хранилище через
// onRefreshed), но неудача сессию НЕ завершает: ни плашки «сессия истекла»,
// ни второго выхода — что стереть, экран решает сам. Отказ — бросок.
export function refreshSessionForLogout(): Promise<string> {
  return sessionRefresh.refreshAccessToken({ endSessionOnFailure: false });
}

// WEB-1217 (хвост 16.08) — deps for the extracted decision factory (see
// emailNotVerifiedRetry.ts's docstring for why it's split out). Built once,
// same as sessionRefresh above: hasStoredRefreshToken/refreshAccessToken
// reuse the very same storage/coordinator the 401 branch already uses.
const emailNotVerifiedRetryDeps: EmailNotVerifiedRetryDeps = {
  hasStoredRefreshToken: () => !!refreshTokenStorage.get(),
  // WEB-1848 — the speculative refresh behind an EMAIL_NOT_VERIFIED failure
  // must never END the session: `endSessionOnFailure: false`. Only the 401
  // branch above owns that verdict. Before this, a rejected refresh here
  // fired onSessionExpired → toast + logoutThunk, logging out a brand-new
  // user whose session had just answered GET /auth/me with 200.
  refreshAccessToken: () =>
    sessionRefresh.refreshAccessToken({ endSessionOnFailure: false }),
  // WEB-1848 — exempt by failing URL OR by the screen the user is on: the
  // invite screen's own siblings (App.tsx's GET /projects) used to redirect
  // the user off it. See emailNotVerifiedFallback.ts.
  isExemptUrl: (url) =>
    isEmailNotVerifiedFallbackExemptUrl(url) ||
    isEmailNotVerifiedFallbackExemptLocation(
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    ),
};

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  // WEB-1217 — separate from `_retry` (401 path) on purpose: a request can
  // legitimately need both a 401-retry AND an EMAIL_NOT_VERIFIED-retry
  // across its lifetime, and conflating the two flags would cap it at one
  // retry total instead of one PER failure kind.
  _emailNotVerifiedRetry?: boolean;
}

/**
 * WEB-1217 — surface the "please verify your email" message and land the
 * user on the verify screen. A plain `window.location.href` (not a router
 * navigate()): this interceptor runs outside React, same constraint as the
 * cross-host redirects in router/AdminRoute.tsx.
 */
function fallBackToVerifyEmailScreen(): void {
  notifyApiError(pickStrings(errorsStrings).verifyEmailToContinue);
  window.location.href = emailNotVerifiedRedirectUrl(tokenStorage.get());
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const status = error.response?.status;
    // WEB-2590: истёкший предел ожидания — фразой из словаря на языке
    // кабинета, а не «timeout of 30000ms exceeded». Статуса нет: автосохранение
    // считает такой отказ временным и повторяет по своим правилам.
    if (isRequestTimeoutError(error)) {
      return Promise.reject(Object.assign(new Error(requestTimeoutMessage(error)), { timedOut: true }));
    }
    // WEB-2469: отказ выгрузки (`responseType: 'blob'`) — JSON внутри Blob;
    // разбираем ДО чтения code/message ниже, иначе код не доходит до человека.
    if (error.response) error.response.data = await readBlobErrorBody(error.response.data);

    // AC1 — a request that failed only because the access token expired is
    // retried transparently once, after a silent refresh (one attempt per
    // request, see sessionRefresh.ts). A refused refresh falls through to the
    // flattened rejection below; a hung/dropped one rejects with its own
    // dictionary phrase (WEB-2613).
    const afterRefresh = await sessionRefresh.handleUnauthorized(error);
    if (afterRefresh !== SESSION_REFRESH_PASS) return afterRefresh;

    // WEB-1217 (хвост 16.08) — thin wiring only; the actual decision
    // (retry / redirect / pass) is made by the extracted, unit-tested
    // factory in emailNotVerifiedRetry.ts. See that module's docstring for
    // why this is split out and screen-map-auth.md for the user-facing
    // behavior. `alreadyRetried` mirrors whether THIS invocation is itself
    // the retried request recursing back into the interceptor.
    const emailNotVerifiedCode = error.response?.data?.code;
    if (
      originalRequest &&
      status === 403 &&
      emailNotVerifiedCode === 'EMAIL_NOT_VERIFIED'
    ) {
      const alreadyRetried = !!originalRequest._emailNotVerifiedRetry;
      if (!alreadyRetried) {
        originalRequest._emailNotVerifiedRetry = true;
      }
      const decision = await resolveEmailNotVerifiedRetry(
        {
          status,
          code: emailNotVerifiedCode,
          url: originalRequest.url,
          alreadyRetried,
        },
        emailNotVerifiedRetryDeps,
      );
      if (decision.action === 'retry') {
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${decision.accessToken}`;
        return await api(originalRequest);
      }
      if (decision.action === 'redirect') {
        fallBackToVerifyEmailScreen();
      }
    }

    const serverMessage = error.response?.data?.message;
    const rawMessage = Array.isArray(serverMessage) ? serverMessage[0] : serverMessage;
    // WEB-1091 §2/AC8: admin-panel error codes (ADMIN_2FA_REQUIRED and any
    // future sibling) are UPPER_SNAKE machine strings on the backend — never
    // show them verbatim. No-op for every other message (the vast majority).
    // WEB-754 (доделка аудита 22.08, п.2): отказ сторожа статуса по
    // замороженному проекту приходит машинным кодом PROJECT_FROZEN и
    // английской строкой «This project is frozen…» — человеку показываем
    // русское объяснение со следующим шагом (см. serverErrorMessages.ts).
    // Слой поверх admin-перевода: ключи разные (код vs сама строка), пересечься
    // не могут; неизвестный код проходит насквозь без изменений.
    const message = friendlyServerErrorMessage(
      error.response?.data?.code,
      friendlyAdminErrorMessage(rawMessage),
    );
    const flattened = new Error(message || error.message);
    // Preserve the HTTP status so callers can branch on it (e.g. 402 vs 404)
    // after the response shape has been flattened away. (`status` already
    // computed above, for the 401-retry check.)
    if (typeof status === 'number') {
      (flattened as Error & { status?: number }).status = status;
    }
    // WEB-391: preserve the server error code (e.g. 'TIER_LIMIT') so callers can
    // distinguish a tier-limit upsell from a plain wallet-balance 402.
    const code = error.response?.data?.code;
    if (typeof code === 'string') {
      (flattened as Error & { code?: string }).code = code;
    } else if (rawMessage === 'ADMIN_2FA_REQUIRED') {
      // WEB-1091 §2/AC8: Nest's plain ForbiddenException(message) body has no
      // separate `code` field (unlike the structured TIER_LIMIT responses
      // above) — the raw message itself IS the code. Preserve it under
      // `.code` too so a caller that wants to react specially (e.g. render
      // its own "go to settings" button) can branch on the stable code
      // rather than string-matching the now-translated `message`.
      (flattened as Error & { code?: string }).code = rawMessage;
    }
    // WEB-1908: рядом с общим `code` сервер шлёт `reasonCode` — ПРИЧИНУ
    // денежного отказа 402 (кончились проекты/квизы/пейволлы/воронки/домены,
    // публикация на бесплатном тарифе). Общий код выбирает ОКНО
    // (`utils/tierLimit.ts`, шрам WEB-135) и потому остаётся как есть;
    // причина нужна, чтобы показать человеку, что именно упёрлось, на языке
    // кабинета (`utils/localizeError.ts`) вместо английского текста сервера.
    const reasonCode = error.response?.data?.reasonCode;
    if (typeof reasonCode === 'string') {
      (flattened as Error & { reasonCode?: string }).reasonCode = reasonCode;
    }
    // WEB-386: preserve the structured usage/limit numbers (present alongside
    // TIER_LIMIT) so callers can build a localized message with the actual
    // limit instead of showing the backend's English `reason` string verbatim.
    const usage = error.response?.data?.usage;
    if (typeof usage === 'number') {
      (flattened as Error & { usage?: number }).usage = usage;
    }
    const limit = error.response?.data?.limit;
    if (typeof limit === 'number') {
      (flattened as Error & { limit?: number }).limit = limit;
    }
    // WEB-2491 К6в: машинные подробности отказа (`humanError(…, details)` на
    // сервере) — по ним форма пикселя подсвечивает столкнувшиеся строки имён.
    const details = error.response?.data?.details;
    if (typeof details === 'object' && details !== null && !Array.isArray(details)) {
      (flattened as Error & { details?: Record<string, unknown> }).details = details;
    }
    return Promise.reject(flattened);
  }
);
